import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiAward,
  FiUsers,
  FiTrendingUp,
  FiDollarSign,
  FiActivity,
  FiClock,
  FiBarChart2,
  FiPieChart,
  FiUser,
  FiImage,
  FiCalendar
} from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Loader } from '../components/Common/Loader';
import { ErrorDisplay } from '../components/Common/ErrorDisplay';
import SignalCard from '../components/Community/SignalCard';
import ProfitCalculatorPanel from '../components/Trading/ProfitCalculatorPanel';
import { useApi } from '../hooks/useApi';
import api from '../api/client';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../utils/imageUtils';
import ProfitLoss from '../components/Dashboard/ProfitLoss';

interface PerformancePoint {
  index?: number;
  date?: string;
  label?: string;
  cumulativePl: number;
}

interface MasterDetails {
  _id: string;
  name: string;
  email: string;
  accountBalance: number;
  totalTrades: number;
  successRate: number;
  profitableTrades: number;
  losingTrades: number;
  followerCount: number;
  isAlpacaConnected: boolean;
  isCurrentUserFollowing: boolean;
  createdAt: string;
  alpacaAccount: any;
  positions: any[];
  positionsSource?: 'live' | 'database' | 'live_error';
  riskMetrics?: {
    maxDrawdownPercent: number;
    maxDrawdownAbsolute: number;
    openExposurePercent: number;
    openNotionalExposure: number;
  };
  performanceCurve?: PerformancePoint[];
  portfolioHistory: any;
  recentTrades: any[];
  followers: any[];
  profileImage?: string;
}

const MasterDetailsPage: React.FC = () => {
  const { masterId } = useParams<{ masterId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'positions' | 'history' | 'followers' | 'signals' | 'calculator'>('overview');
  const [signalsPage, setSignalsPage] = useState(1);

  // Fetch master details
  const { data, isLoading, error } = useQuery({
    queryKey: ['masterDetails', masterId],
    queryFn: async () => {
      const response = await api.get(`/master-details/${masterId}`);
      return response.data;
    },
    staleTime: 60 * 1000, // Refresh often for live OANDA / Webull positions
    gcTime: 10 * 60 * 1000
  });

  const masterDetails: MasterDetails | undefined = data?.masterDetails;

  // Fetch master signals (always fetch to show count in tab, but only show content when tab is active)
  const { data: signalsData, refetch: refetchSignals } = useApi.useMasterSignals(masterId || '', signalsPage, 10, undefined, {
    enabled: !!masterId, // Always fetch when masterId is available to show count in tab
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
  });
  const signals = signalsData?.signals || [];

  // Listen for new signals via WebSocket and refetch
  React.useEffect(() => {
    if (!masterId || activeTab !== 'signals') return;

    import('../services/socketService').then(({ socketService }) => {
      const socket = socketService.getSocket();
      if (!socket) return;

      const handleNewSignal = (data: any) => {
        // Only refetch if the signal is from this master
        if (data.signal?.author?._id === masterId || data.signal?.author === masterId) {
          console.log('📢 MasterDetailsPage: New signal from this master, refetching...');
          setTimeout(() => {
            refetchSignals();
          }, 200);
        }
      };

      const handleLikeUpdate = () => {
        // Refetch when likes are updated
        setTimeout(() => {
          refetchSignals();
        }, 200);
      };

      socket.on('signal:new', handleNewSignal);
      socket.on('signal:like', handleLikeUpdate);

      return () => {
        socket.off('signal:new', handleNewSignal);
        socket.off('signal:like', handleLikeUpdate);
      };
    });
  }, [masterId, activeTab, refetchSignals]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader message="Loading master details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorDisplay error="Failed to load master details" />
      </div>
    );
  }

  if (!masterDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorDisplay error="Master trader not found" />
      </div>
    );
  }

  const alpacaAccount = masterDetails.alpacaAccount;
  const positionsShowAccountColumn = (masterDetails.positions || []).some((p: any) => p.accountLabel);

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-3 sm:py-8 sm:px-4 overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-6 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="p-2 shrink-0 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
            {masterDetails.profileImage ? (
              <div className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 rounded-full overflow-hidden border-2 border-primary-500 shadow-md">
                <img
                  src={getProfileImageUrl(masterDetails.profileImage)}
                  alt={masterDetails.name}
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                  }}
                />
              </div>
            ) : (
              <div className="p-3 sm:p-4 shrink-0 bg-primary-100 rounded-full">
                <FiAward className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-anywhere">{masterDetails.name}</h1>
              <p className="text-gray-600 break-anywhere">{masterDetails.email}</p>
            </div>
          </div>

        </div>

        {/* Stats Cards - Theme Colors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-6">
          {/* Portfolio Value */}
          <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiDollarSign className="h-6 w-6 opacity-80" />
              <FiTrendingUp className="h-5 w-5 opacity-80" />
            </div>
            <p className="text-sm opacity-90 mb-1">Portfolio Value</p>
            <p className="text-2xl sm:text-3xl font-bold break-anywhere">
              ${alpacaAccount?.portfolio_value ? parseFloat(alpacaAccount.portfolio_value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : masterDetails.accountBalance?.toLocaleString()}
            </p>
            {alpacaAccount?.equity && (
              <p className="text-xs opacity-80 mt-1">
                Equity: ${parseFloat(alpacaAccount.equity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Buying Power */}
          <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiBarChart2 className="h-6 w-6 opacity-80" />
            </div>
            <p className="text-sm opacity-90 mb-1">Buying Power</p>
            <p className="text-2xl sm:text-3xl font-bold break-anywhere">
              ${alpacaAccount?.buying_power ? parseFloat(alpacaAccount.buying_power).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
            </p>
            {alpacaAccount?.cash && (
              <p className="text-xs opacity-80 mt-1">
                Cash: ${parseFloat(alpacaAccount.cash).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Followers */}
          <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiUsers className="h-6 w-6 opacity-80" />
            </div>
            <p className="text-sm opacity-90 mb-1">Followers</p>
            <p className="text-2xl sm:text-3xl font-bold break-anywhere">{masterDetails.followerCount || 0}</p>
            <p className="text-xs opacity-80 mt-1">{masterDetails.followerCount || 0} Active copy traders</p>
          </div>

          {/* Total Trades */}
          <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiActivity className="h-6 w-6 opacity-80" />
            </div>
            <p className="text-sm opacity-90 mb-1">Total Trades</p>
            <p className="text-2xl sm:text-3xl font-bold break-anywhere">{masterDetails.totalTrades || 0}</p>
            <p className="text-xs opacity-80 mt-1">Success Rate: {masterDetails.successRate?.toFixed(1) || 0}%</p>
          </div>

          {/* Max drawdown (historical, from realized P/L series) */}
          <div className="card bg-gradient-to-br from-slate-700 to-slate-800 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiTrendingUp className="h-6 w-6 opacity-80 rotate-180" />
            </div>
            <p className="text-sm opacity-90 mb-1">Max Drawdown</p>
            <p className="text-2xl sm:text-3xl font-bold break-anywhere">
              {masterDetails.riskMetrics?.maxDrawdownPercent != null
                ? `${masterDetails.riskMetrics.maxDrawdownPercent.toFixed(1)}%`
                : '—'}
            </p>
            <p className="text-xs opacity-80 mt-1">
              Peak-to-trough on cumulative realized P/L
            </p>
          </div>

          {/* Open exposure vs balance */}
          <div className="card bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiBarChart2 className="h-6 w-6 opacity-80" />
            </div>
            <p className="text-sm opacity-90 mb-1">Open Exposure</p>
            <p className="text-2xl sm:text-3xl font-bold break-anywhere">
              {masterDetails.riskMetrics?.openExposurePercent != null
                ? `${masterDetails.riskMetrics.openExposurePercent.toFixed(1)}%`
                : '—'}
            </p>
            <p className="text-xs opacity-80 mt-1">
              Notional open / account balance
            </p>
          </div>
        </div>

        {/* Brokerage Detailed Account Info (Only if available) */}
        {alpacaAccount && alpacaAccount.maintenance_margin && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiPieChart className="h-5 w-5 text-primary-600" />
              Account Details
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Account Status</p>
                <p className="font-semibold text-gray-900 uppercase">{alpacaAccount.status || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Pattern Day Trader</p>
                <p className="font-semibold text-gray-900">{alpacaAccount.pattern_day_trader ? 'Yes' : 'No'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Daytrade Count</p>
                <p className="font-semibold text-gray-900">{alpacaAccount.daytrade_count || 0}</p>
              </div>
              {/* Only show margin info if relevant */}
              {alpacaAccount.initial_margin && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Initial Margin</p>
                  <p className="font-semibold text-gray-900">
                    ${parseFloat(alpacaAccount.initial_margin).toLocaleString()}
                  </p>
                </div>
              )}
              {alpacaAccount.maintenance_margin && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Maintenance Margin</p>
                  <p className="font-semibold text-gray-900">
                    ${parseFloat(alpacaAccount.maintenance_margin).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="card min-w-0">
          <div className="flex gap-1 sm:gap-2 border-b border-gray-200 mb-6 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3 sm:px-6 py-2 sm:py-3 shrink-0 font-semibold transition-colors whitespace-nowrap ${activeTab === 'overview'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('positions')}
              className={`px-3 sm:px-6 py-2 sm:py-3 shrink-0 font-semibold transition-colors whitespace-nowrap ${activeTab === 'positions'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Open Positions ({masterDetails.positions?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-3 sm:px-6 py-2 sm:py-3 shrink-0 font-semibold transition-colors whitespace-nowrap ${activeTab === 'history'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Trade History
            </button>
            <button
              onClick={() => setActiveTab('followers')}
              className={`px-3 sm:px-6 py-2 sm:py-3 shrink-0 font-semibold transition-colors whitespace-nowrap ${activeTab === 'followers'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Followers ({masterDetails.followers?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('signals')}
              className={`px-3 sm:px-6 py-2 sm:py-3 shrink-0 font-semibold transition-colors whitespace-nowrap ${activeTab === 'signals'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Signals & Posts
              <span className="ml-1.5 text-sm font-normal">
                ({signalsData?.pagination?.total ?? 0})
              </span>
            </button>
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-3 sm:px-6 py-2 sm:py-3 shrink-0 font-semibold transition-colors whitespace-nowrap ${activeTab === 'calculator'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Profit Calculator
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'calculator' && masterId && (
            <ProfitCalculatorPanel masterId={masterId} masterName={masterDetails.name} />
          )}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Cumulative realized P/L — line chart */}
              {masterDetails.performanceCurve && masterDetails.performanceCurve.length > 0 ? (
                <div className="bg-white rounded-lg p-3 sm:p-6 border border-gray-200 shadow-sm min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <FiActivity className="h-5 w-5 text-primary-600" />
                    Performance (cumulative realized P/L)
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Built from recorded master trades. Drawdown card uses the worst peak-to-trough drop on this curve.
                  </p>
                  <div className="h-[300px] w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={masterDetails.performanceCurve} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
                        />
                        <Tooltip
                          formatter={(value: number) => [`$${Number(value).toFixed(2)}`, 'Cumulative P/L']}
                          labelFormatter={(_, payload) =>
                            payload?.[0]?.payload?.date
                              ? new Date(payload[0].payload.date).toLocaleString()
                              : ''
                          }
                          contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="cumulativePl"
                          stroke="#2563eb"
                          strokeWidth={2}
                          dot={{ r: 4, fill: '#2563eb', strokeWidth: 0 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : null}

              {/* Monthly P&L calendar — same metrics as follower Profit & Loss tab, for this master */}
              {masterId && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-w-0">
                  <div className="px-3 pt-4 sm:px-6 sm:pt-6 pb-2">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FiCalendar className="h-5 w-5 text-primary-600" />
                      Monthly performance
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Daily realized P/L and trade counts (UTC) from this master&apos;s recorded trades.
                    </p>
                  </div>
                  <div className="px-2 pb-4 min-w-0">
                    <ProfitLoss selectedBrokerId={null} masterUserId={masterId} />
                  </div>
                </div>
              )}

              {/* Performance Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Success Rate Chart */}
                <div className="bg-white rounded-lg p-3 sm:p-6 border border-gray-200 shadow-sm min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FiPieChart className="h-5 w-5 text-primary-600" />
                    Success Distribution
                  </h3>
                  {(() => {
                    const profitable = masterDetails.profitableTrades || 0;
                    const losing = masterDetails.losingTrades || 0;
                    const total = profitable + losing;

                    if (total > 0 || masterDetails.totalTrades > 0) {
                      const profitableCount = total > 0 ? profitable : (masterDetails.totalTrades > 0 ? Math.round((masterDetails.successRate || 0) / 100 * masterDetails.totalTrades) : 0);
                      const losingCount = total > 0 ? losing : (masterDetails.totalTrades - profitableCount);
                      const unknownData = (total === 0 && masterDetails.totalTrades > 0);

                      return (
                        <div className="space-y-4">
                          <div className="h-[250px] w-full min-w-0">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: 'Profitable', value: Math.max(profitableCount, 0), fill: '#10b981' },
                                    { name: 'Losing', value: Math.max(losingCount, 0), fill: '#ef4444' }
                                  ]}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={80}
                                  paddingAngle={5}
                                  dataKey="value"
                                >
                                  <Cell fill="#10b981" />
                                  <Cell fill="#ef4444" />
                                </Pie>
                                <Tooltip
                                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                />
                                <Legend verticalAlign="bottom" height={36} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>

                          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center border-t border-gray-100 pt-4">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Success Rate</p>
                              <p className="text-2xl font-bold text-gray-900">{masterDetails.successRate?.toFixed(1) || 0}%</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Profitable</p>
                              <p className="text-2xl font-bold text-green-600">{profitableCount}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Losing</p>
                              <p className="text-2xl font-bold text-red-600">{losingCount}</p>
                            </div>
                          </div>
                          {unknownData && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                              <p className="text-sm text-yellow-800">
                                Trade outcomes pending closure.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div className="flex items-center justify-center h-64 text-gray-400 flex-col">
                        <FiPieChart className="h-12 w-12 mb-2 opacity-50" />
                        <p>No trade data available</p>
                      </div>
                    );
                  })()}
                </div>

                {/* Position Distribution Chart */}
                <div className="bg-white rounded-lg p-3 sm:p-6 border border-gray-200 shadow-sm min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FiPieChart className="h-5 w-5 text-primary-600" />
                    Position Distribution
                  </h3>
                  {masterDetails.positions && masterDetails.positions.length > 0 ? (
                    <div className="h-[320px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={masterDetails.positions.slice(0, 10).map((pos: any) => {
                              const pieMv =
                                pos.market_value ??
                                pos.marketValue ??
                                (Number(pos.qty) || 0) * (Number(pos.current_price ?? pos.currentPrice) || 0);
                              return {
                                name: typeof pos.symbol === 'string' ? pos.symbol : pos.symbol?.symbol || '—',
                                value: Math.abs(Number(pieMv) || 0)
                              };
                            })}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => percent > 0.05 ? `${name}` : ''}
                            outerRadius={80}
                            dataKey="value"
                          >
                            {masterDetails.positions.slice(0, 10).map((_: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#6366f1'][index % 7]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => `$${parseFloat(value.toString()).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                          <Legend layout="vertical" align="right" verticalAlign="middle" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400 flex-col">
                      <FiPieChart className="h-12 w-12 mb-2 opacity-50" />
                      <p>No open positions</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Trade Volume Bar Chart */}
              {masterDetails.recentTrades && masterDetails.recentTrades.length > 0 && (
                <div className="bg-white rounded-lg p-3 sm:p-6 border border-gray-200 shadow-sm min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FiBarChart2 className="h-5 w-5 text-primary-600" />
                    Recent Trade Volume
                  </h3>
                  <div className="h-[300px] w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={masterDetails.recentTrades.slice(0, 10).reverse().map((trade: any) => ({
                        symbol: trade.symbol,
                        quantity: trade.qty,
                        side: trade.side,
                        date: new Date(trade.createdAt).toLocaleDateString()
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="symbol" axisLine={false} tickLine={false} />
                        <YAxis axisLine={false} tickLine={false} />
                        <Tooltip
                          cursor={{ fill: '#f3f4f6' }}
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        />
                        <Bar
                          dataKey="quantity"
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                          name="Shares/Contracts"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'positions' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-lg font-bold text-gray-900">Open Positions</h3>
                {masterDetails.positionsSource === 'live' && (
                  <span className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded bg-emerald-100 text-emerald-800">
                    Live · OANDA / SnapTrade
                  </span>
                )}
                {masterDetails.positionsSource === 'live_error' && (
                  <span className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded bg-amber-100 text-amber-900">
                    Broker unavailable
                  </span>
                )}
                {masterDetails.positionsSource === 'database' && (
                  <span className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded bg-gray-200 text-gray-700">
                    From trade history (no OANDA/SnapTrade link)
                  </span>
                )}
              </div>
              {masterDetails.positionsSource === 'live_error' && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Could not load open positions from the broker. Check the connection or try again shortly.
                </p>
              )}
              {masterDetails.positions && masterDetails.positions.length > 0 ? (
                <div className="table-scroll overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full min-w-[640px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {positionsShowAccountColumn && (
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Account</th>
                        )}
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Symbol</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Quantity</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Avg Entry</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Last Price</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Est. Mkt Value</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Est. P&L</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {masterDetails.positions.map((position: any, idx: number) => {
                        const unrealizedPL = parseFloat(position.unrealized_pl ?? position.unrealizedPl ?? 0);
                        const isProfit = unrealizedPL >= 0;
                        const avgEn = parseFloat(position.avg_entry_price ?? position.avgEntryPrice ?? 0);
                        const curPx = parseFloat(position.current_price ?? position.currentPrice ?? 0);
                        const mkt = parseFloat(position.market_value ?? position.marketValue ?? 0);
                        const sym = typeof position.symbol === 'string' ? position.symbol : position.symbol?.symbol || '—';

                        return (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            {positionsShowAccountColumn && (
                              <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px] truncate" title={position.accountLabel}>
                                {position.accountLabel || '—'}
                              </td>
                            )}
                            <td className="px-4 py-3 font-bold text-gray-900">{sym}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{position.qty}</td>
                            <td className="px-4 py-3 text-right text-gray-700">
                              ${avgEn.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700">
                              ${curPx.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              ${mkt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className={`px-4 py-3 text-right font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                              {isProfit ? '+' : ''}${unrealizedPL.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="p-2 bg-gray-50 text-xs text-gray-500 text-center">
                    {masterDetails.positionsSource === 'live'
                      ? 'Live positions from OANDA or SnapTrade (e.g. Webull). Values come from the broker.'
                      : masterDetails.positionsSource === 'live_error'
                      ? 'Expected live broker data but the request failed.'
                      : 'Positions estimated from trade history — master has no active OANDA/SnapTrade connection, or use Overview for charts.'}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No open positions</p>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Trade History</h3>
              {masterDetails.recentTrades && masterDetails.recentTrades.length > 0 ? (
                <div className="table-scroll overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full min-w-[720px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date/Time</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Symbol</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Side</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Quantity</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Filled Price</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">P&L</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {masterDetails.recentTrades.map((trade: any) => (
                        <tr key={trade._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {new Date(trade.createdAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-bold text-gray-900">{trade.symbol}</td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${trade.side === 'buy'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                                }`}
                            >
                              {trade.side}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">{trade.qty}</td>
                          <td className="px-4 py-3 text-right text-gray-700 font-medium">
                            ${trade.filledPrice ? parseFloat(trade.filledPrice).toFixed(2) : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {trade.profitLoss !== undefined && trade.profitLoss !== 0 ? (
                              <span className={trade.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {trade.profitLoss >= 0 ? '+' : ''}${trade.profitLoss.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold uppercase ${trade.status === 'filled'
                                ? 'bg-green-100 text-green-700'
                                : trade.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : trade.status === 'partial'
                                    ? 'bg-blue-100 text-blue-700'
                                    : trade.status === 'failed'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-700'
                                }`}
                            >
                              {trade.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No trade history</p>
              )}
            </div>
          )}

          {activeTab === 'followers' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FiUsers className="h-5 w-5 text-primary-600" />
                Followers
              </h3>
              {masterDetails.followers && masterDetails.followers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {masterDetails.followers.map((follower: any) => (
                    <div key={follower._id} className="card min-w-0 border-2 border-gray-200 hover:border-primary-300 transition-colors">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 shrink-0 bg-purple-100 rounded-full">
                          <FiUser className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-gray-900">Anonymous</h4>
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                              follower.status === 'paused'
                                ? 'bg-amber-100 text-amber-700'
                                : follower.status === 'pending'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {follower.status === 'paused'
                              ? 'Paused'
                              : follower.status === 'pending'
                              ? 'Pending'
                              : 'Active'}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Allocation:</span>
                          <span className="font-semibold text-gray-900">{follower.allocationPercent}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Max Investment:</span>
                          <span className="font-semibold text-gray-900">
                            ${follower.maxInvestmentAmount?.toLocaleString() || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Account Balance:</span>
                          <span className="font-semibold text-gray-900">
                            ${follower.accountBalance?.toLocaleString() || '0'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Following Since:</span>
                          <span className="font-semibold text-gray-900">
                            {new Date(follower.startDate).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FiUsers className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No followers yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'signals' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex flex-wrap items-center gap-2">
                <FiTrendingUp className="h-5 w-5 text-primary-600" />
                Signals & Posts
                {signalsData?.pagination && signalsData.pagination.total > 0 && (
                  <span className="ml-2 text-base text-primary-500 dark:text-primary-400">
                    ({signalsData.pagination.total})
                  </span>
                )}
              </h3>
              {signals.length > 0 ? (
                <>
                  <div className="space-y-4">
                    {signals.map((signal) => (
                      <SignalCard key={signal._id} signal={signal} />
                    ))}
                  </div>
                  {signalsData?.pagination && signalsData.pagination.pages > 1 && (
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
                      <button
                        onClick={() => setSignalsPage(p => Math.max(1, p - 1))}
                        disabled={signalsPage === 1}
                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                        Page {signalsPage} of {signalsData.pagination.pages}
                      </span>
                      <button
                        onClick={() => setSignalsPage(p => Math.min(signalsData.pagination.pages, p + 1))}
                        disabled={signalsPage === signalsData.pagination.pages}
                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <FiTrendingUp className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No signals or posts yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterDetailsPage;
